import { Button } from '@deepguide-ai/ui/button'
import { Label } from '@deepguide-ai/ui/label'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { Input } from '@deepguide-ai/ui/input'
import { MultiSelect } from '@deepguide-ai/ui/multi-select'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'

type ReviewMember = { userId: string; role: string; user: { name: string | null; email: string; isServiceAccount?: boolean } }

export function ReviewContextFields({ idPrefix, notes, setNotes, participants, setParticipants, attachments, removeAttachment, uploadFile,
  pending, isUploading, currentUserId, members, membersLoading, membersError }: {
  idPrefix: string; notes: string; setNotes: (value: string) => void;
  participants: string[]; setParticipants: (value: string[]) => void;
  attachments: Array<{ id: string; name: string }>; removeAttachment: (id: string) => void;
  uploadFile: (file: File) => unknown; pending: boolean; isUploading: boolean;
  currentUserId?: string; members?: ReviewMember[]; membersLoading: boolean; membersError: Error | null;
}) {
  return <>
    <div className='space-y-2'>
      <Label htmlFor={`${idPrefix}-notes`}>Review notes (optional)</Label>
      <Textarea id={`${idPrefix}-notes`} value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={5000} rows={3} disabled={pending} placeholder='Describe the areas checked and why no changes are needed.' />
    </div>
    <div className='space-y-2'>
      <Label htmlFor={`${idPrefix}-participants`}>Additional participants (optional)</Label>
      <MultiSelect id={`${idPrefix}-participants`} modalPopover hideClearAndClose maxCount={2} value={participants} onValueChange={setParticipants}
        options={(members ?? []).filter((member) => member.userId !== currentUserId && member.role !== 'AUDITOR' && !member.user.isServiceAccount).map((member) => ({
          value: member.user.email,
          label: member.user.name || member.user.email,
          description: member.user.name ? member.user.email : undefined,
          icon: () => <MemberAvatar person={member.user} size='xs' className='shrink-0' />,
        }))}
        placeholder='Select other participants' disabled={pending || membersLoading || !!membersError} />
      {membersError && <p role='alert' className='text-sm text-destructive'>{membersError.message}</p>}
      <p className='text-xs text-muted-foreground'>You are recorded automatically. Other participants are not additional approvers or signers.</p>
    </div>
    <div className='space-y-2'>
      <Label htmlFor={`${idPrefix}-attachment`}>Attachments (optional)</Label>
      <Input id={`${idPrefix}-attachment`} type='file' disabled={pending || isUploading || attachments.length >= 10} onChange={(event) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (file) void uploadFile(file)
      }} />
      <p className='text-xs text-muted-foreground'>{isUploading ? 'Uploading…' : 'Attach meeting notes or supporting review records. Up to 10 files.'}</p>
      {attachments.map((file) => <div key={file.id} className='flex items-center justify-between gap-2 text-sm'>
        <span className='min-w-0 truncate'>{file.name}</span>
        <Button variant='ghost' size='sm' disabled={pending || isUploading} aria-label={`Remove ${file.name}`} onClick={() => removeAttachment(file.id)}>Remove</Button>
      </div>)}
    </div>
  </>
}
